import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  arrayRemove, arrayUnion, doc, getDoc, setDoc, updateDoc,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { db, storage } from "../firebase";
import { useAuth } from "../auth/AuthProvider";
import { friendly } from "../lib/errors";
import { captureLocation, geoErrorMessage } from "../lib/geo";
import {
  GENDERS, MAX_PHOTOS, MIN_PHOTOS, PROMPT_BANK, RADII, radiusLabel, type Gender, type PromptAnswer,
} from "../lib/models";
import { responsivenessBand, scoreColor } from "../lib/bands";
import { enablePush, pushPermission, type PushStatus } from "../lib/push";
import type { DeckCard, UserProfile } from "../lib/types";
import Gauge from "../components/Gauge";
import ProfileDetail from "../components/ProfileDetail";
import { ResponsivenessInfoSheet } from "../components/InfoSheets";

type AutosaveState = "idle" | "saving" | "saved" | "incomplete";

/**
 * Profile tab — ported from ProfileSetupView.swift (edit mode): your own
 * responsiveness gauge, every profile field with live auto-save, the eye
 * (preview your card) top-left, and the gear (Account Settings) top-right.
 */
export default function Profile() {
  const { uid } = useAuth();
  const navigate = useNavigate();

  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Editable fields (mirrors iOS editableData()).
  const [name, setName] = useState("");
  const [headline, setHeadline] = useState("");
  const [bio, setBio] = useState("");
  const [prompts, setPrompts] = useState<PromptAnswer[]>([
    { prompt: "", answer: "" }, { prompt: "", answer: "" }, { prompt: "", answer: "" },
  ]);
  const [gender, setGender] = useState<Gender | "">("");
  const [seeking, setSeeking] = useState<Gender[]>([]);
  const [radius, setRadius] = useState(25);
  const [ageMin, setAgeMin] = useState(18);
  const [ageMax, setAgeMax] = useState(99);
  const [photos, setPhotos] = useState<string[]>([]);
  const [locationName, setLocationName] = useState<string | null>(null);
  const [hasLocation, setHasLocation] = useState(false);
  const [age, setAge] = useState<number | null>(null);

  const [uploading, setUploading] = useState(false);
  const [locBusy, setLocBusy] = useState(false);
  const [autosave, setAutosave] = useState<AutosaveState>("idle");
  const [myResponsiveness, setMyResponsiveness] = useState<number | null>(null);
  const [pushState, setPushState] = useState<PushStatus | "idle">(
    pushPermission() === "granted" ? "enabled" : "idle",
  );
  const [showRespInfo, setShowRespInfo] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const lastSaved = useRef("");
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const promptsComplete = prompts.every((p) => p.prompt && p.answer.trim());
  const formComplete = !!name && !!gender && seeking.length > 0 && promptsComplete && photos.length >= MIN_PHOTOS;

  const editableData = useCallback(() => ({
    displayName: name,
    headline,
    bio,
    searchRadiusMiles: radius,
    ageMin,
    ageMax,
    gender,
    seeking: [...seeking].sort(),
    prompts: prompts.filter((p) => p.prompt && p.answer).map((p) => ({ prompt: p.prompt, answer: p.answer })),
  }), [name, headline, bio, radius, ageMin, ageMax, gender, seeking, prompts]);

  const snapshot = useCallback(() => JSON.stringify(editableData()), [editableData]);

  // ---- load ----
  useEffect(() => {
    if (!uid) return;
    void getDoc(doc(db, "users", uid))
      .then((s) => {
        const d = (s.data() ?? {}) as UserProfile & { matchCount?: number; engagedThreads?: number; age?: number };
        setName(d.displayName ?? "");
        setHeadline(d.headline ?? "");
        setBio(d.bio ?? "");
        const slots: PromptAnswer[] = [
          { prompt: "", answer: "" }, { prompt: "", answer: "" }, { prompt: "", answer: "" },
        ];
        (d.prompts ?? []).slice(0, 3).forEach((p, i) => { slots[i] = { prompt: p.prompt, answer: p.answer }; });
        setPrompts(slots);
        setGender((d.gender as Gender) ?? "");
        setSeeking((d.seeking as Gender[]) ?? []);
        setRadius(d.searchRadiusMiles ?? 25);
        setAgeMin(d.ageMin ?? 18);
        setAgeMax(d.ageMax ?? 99);
        setPhotos(d.photoURLs ?? []);
        setLocationName(d.locationName ?? null);
        setHasLocation(typeof d.lat === "number");
        setAge(d.age ?? null);
        // Lifetime responsiveness: engaged / matches, shown from 3 matches (iOS).
        const m = d.matchCount ?? 0;
        const e = d.engagedThreads ?? 0;
        setMyResponsiveness(m >= 3 ? Math.round(Math.min(1, e / m) * 100) : null);
        setLoaded(true);
        setAutosave("saved");
        // Snapshot AFTER state settles (microtask) so load doesn't trigger a save.
        setTimeout(() => { lastSaved.current = JSON.stringify({
          displayName: d.displayName ?? "", headline: d.headline ?? "", bio: d.bio ?? "",
          searchRadiusMiles: d.searchRadiusMiles ?? 25, ageMin: d.ageMin ?? 18, ageMax: d.ageMax ?? 99,
          gender: (d.gender as Gender) ?? "", seeking: [...((d.seeking as Gender[]) ?? [])].sort(),
          prompts: slots.filter((p) => p.prompt && p.answer),
        }); }, 0);
      })
      .catch((e) => setError(friendly(e)));
  }, [uid]);

  // ---- debounced autosave (0.7s, like iOS) ----
  useEffect(() => {
    if (!loaded || !uid) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const snap = snapshot();
      if (snap === lastSaved.current) return;
      if (!formComplete) { setAutosave("incomplete"); return; }
      setAutosave("saving");
      void updateDoc(doc(db, "users", uid), editableData())
        .then(() => { lastSaved.current = snap; setAutosave("saved"); })
        .catch((e) => { setAutosave("idle"); setError(friendly(e)); });
    }, 700);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [snapshot, loaded, uid, formComplete, editableData]);

  // ---- photos (atomic add/remove, like iOS arrayUnion/arrayRemove) ----
  async function addPhotos(files: FileList | null) {
    if (!files || !uid) return;
    setUploading(true);
    setError(null);
    try {
      const room = MAX_PHOTOS - photos.length;
      const added: string[] = [];
      for (const file of Array.from(files).slice(0, room)) {
        if (!file.type.startsWith("image/")) continue;
        if (file.size > 10 * 1024 * 1024) { setError("Photos must be under 10 MB."); continue; }
        const r = ref(storage, `users/${uid}/photos/${crypto.randomUUID()}`);
        await uploadBytes(r, file, { contentType: file.type });
        added.push(await getDownloadURL(r));
      }
      if (added.length) {
        await setDoc(doc(db, "users", uid), { photoURLs: arrayUnion(...added) }, { merge: true });
        const fresh = await getDoc(doc(db, "users", uid));
        setPhotos(((fresh.data()?.photoURLs as string[]) ?? []).slice(0, MAX_PHOTOS));
      }
    } catch (e) {
      setError(friendly(e));
    } finally {
      setUploading(false);
    }
  }

  const deleteLocked = photos.length <= MIN_PHOTOS;
  async function removePhoto(url: string) {
    if (!uid || deleteLocked) return;
    const prev = photos;
    setPhotos((p) => p.filter((u) => u !== url));
    try {
      await setDoc(doc(db, "users", uid), { photoURLs: arrayRemove(url) }, { merge: true });
    } catch (e) {
      setPhotos(prev); // put it back — the server still has it
      setError(friendly(e));
    }
  }

  async function updateLocation() {
    if (!uid) return;
    setLocBusy(true);
    setError(null);
    try {
      await captureLocation(uid);
      setHasLocation(true);
    } catch (e) {
      setError(geoErrorMessage(e));
    } finally {
      setLocBusy(false);
    }
  }

  function setPrompt(i: number, patch: Partial<PromptAnswer>) {
    setPrompts((ps) => ps.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  }
  const takenPrompts = useMemo(() => new Set(prompts.map((p) => p.prompt).filter(Boolean)), [prompts]);

  /** The card as others see it, from live editor state (iOS previewProfile). */
  const previewProfile: DeckCard = {
    uid: uid ?? "preview",
    displayName: name,
    age: age ?? 0,
    headline,
    bio,
    prompts: prompts.filter((p) => p.prompt && p.answer),
    photoURLs: photos,
    locationName: locationName ?? "",
    distanceMiles: 0,
    boosted: false,
    compatibility: null,
    responsiveness: null,
  };

  if (!loaded && !error) return <div className="splash"><div className="spinner" /></div>;

  return (
    <div className="screen">
      <div className="profile-header">
        <button className="icon-btn" aria-label="Preview my profile" title="Preview my profile" onClick={() => setShowPreview(true)}>
          👁
        </button>
        <h1 style={{ margin: 0 }}>Profile</h1>
        <button className="icon-btn" aria-label="Account Settings" title="Account Settings" onClick={() => navigate("/settings")}>
          ⚙︎
        </button>
      </div>

      {/* Your responsiveness — the same metric others see on your card. */}
      <h2>Your responsiveness</h2>
      {myResponsiveness !== null ? (
        <button className="resp-row" onClick={() => setShowRespInfo(true)}>
          <Gauge value={myResponsiveness} size={50} color={scoreColor(myResponsiveness)} textColor="var(--text)" />
          <span>
            <span className="resp-band">{responsivenessBand(myResponsiveness)}</span>
            <span className="muted" style={{ display: "block", fontSize: 12 }}>How often you reply after matching</span>
          </span>
          <span className="muted" style={{ marginLeft: "auto" }}>ⓘ</span>
        </button>
      ) : (
        <p className="muted" style={{ fontSize: 13 }}>
          Your responsiveness shows once you've had a few recent matches. Reply to your
          matches to build it up. Others see this on your profile.
        </p>
      )}

      <h2>Photos ({photos.length}/{MAX_PHOTOS}) · min {MIN_PHOTOS}</h2>
      <div className="photo-grid">
        {photos.map((url) => (
          <div className="photo-cell" key={url}>
            <img src={url} alt="" />
            <button
              className="photo-x"
              aria-label="Remove photo"
              disabled={deleteLocked}
              style={deleteLocked ? { opacity: 0.35 } : undefined}
              onClick={() => void removePhoto(url)}
            >✕</button>
          </div>
        ))}
        {photos.length < MAX_PHOTOS && (
          <label className="photo-cell photo-add">
            {uploading ? "…" : "+"}
            <input type="file" accept="image/*" multiple style={{ display: "none" }} onChange={(e) => void addPhotos(e.target.files)} />
          </label>
        )}
      </div>
      <p className="muted" style={{ fontSize: 12 }}>
        {deleteLocked
          ? `Minimum ${MIN_PHOTOS} photos. To swap one, add a new photo before removing.`
          : `Minimum ${MIN_PHOTOS} photos.`}
      </p>

      <h2>Headline</h2>
      <input value={headline} maxLength={60} placeholder="One line that sums you up" onChange={(e) => setHeadline(e.target.value)} />
      <div className="char-count">{headline.length}/60</div>

      <h2>About you</h2>
      <label>First name</label>
      <input value={name} placeholder="First name" onChange={(e) => setName(e.target.value)} />
      <label>About me — what should they spend a message on?</label>
      <textarea value={bio} maxLength={150} onChange={(e) => setBio(e.target.value)} />
      <div className="char-count">{bio.length}/150</div>

      <h2>Three questions to kickstart the connection</h2>
      {prompts.map((p, i) => (
        <div key={i}>
          <select value={p.prompt} onChange={(e) => setPrompt(i, { prompt: e.target.value })}>
            <option value="">Choose question {i + 1}…</option>
            {PROMPT_BANK.filter((q) => q === p.prompt || !takenPrompts.has(q)).map((q) => (
              <option key={q} value={q}>{q}</option>
            ))}
          </select>
          {p.prompt && (
            <textarea
              value={p.answer}
              maxLength={150}
              placeholder="Your answer"
              onChange={(e) => setPrompt(i, { answer: e.target.value })}
            />
          )}
        </div>
      ))}
      <p className="muted" style={{ fontSize: 12 }}>
        They show on your profile as conversation starters.
      </p>

      <h2>I am</h2>
      <select value={gender} onChange={(e) => setGender(e.target.value as Gender)}>
        <option value="">Select…</option>
        {GENDERS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
      </select>

      <h2>Looking for</h2>
      <div className="chip-row">
        {GENDERS.map((g) => (
          <button
            key={g.value}
            className={"chip" + (seeking.includes(g.value) ? " chip-on" : "")}
            onClick={() => setSeeking((s) => (s.includes(g.value) ? s.filter((x) => x !== g.value) : [...s, g.value]))}
          >
            {g.label}{seeking.includes(g.value) ? " ✓" : ""}
          </button>
        ))}
      </div>

      <h2>Location</h2>
      {hasLocation ? (
        <p className="muted" style={{ margin: "2px 0" }}>
          📍 {locationName ?? "Location on"} — Fuse never shows your exact location, only distance.
        </p>
      ) : (
        <p className="muted" style={{ margin: "2px 0", fontSize: 13 }}>
          Fuse shows you people nearby. Your exact location is never shown — only distance.
        </p>
      )}
      <button className="btn btn-secondary" disabled={locBusy} onClick={() => void updateLocation()}>
        {locBusy ? "Locating…" : hasLocation ? "Update my location" : "Enable location services"}
      </button>

      <h2>Notifications</h2>
      {pushState === "enabled" ? (
        <p className="muted" style={{ fontSize: 13, margin: "2px 0" }}>
          ✅ Notifications are on — you'll hear about new matches, messages, and Relays.
        </p>
      ) : (
        <>
          <p className="muted" style={{ fontSize: 13, margin: "2px 0" }}>
            Get pinged for new matches, messages, and when your Relay is ready.
          </p>
          <button
            className="btn btn-secondary"
            onClick={() => { if (uid) void enablePush(uid).then(setPushState); }}
          >
            Enable notifications
          </button>
          {pushState === "denied" && (
            <p className="error" style={{ fontSize: 13 }}>
              Notifications are blocked — allow them for Fuse in iPhone Settings → Notifications.
            </p>
          )}
          {pushState === "unsupported" && (
            <p className="muted" style={{ fontSize: 13 }}>
              This browser can't do push — install Fuse to your Home Screen first (Share →
              Add to Home Screen), then enable here.
            </p>
          )}
          {pushState === "unconfigured" && (
            <p className="error" style={{ fontSize: 13 }}>Push isn't configured in this build yet.</p>
          )}
          {pushState === "error" && (
            <p className="error" style={{ fontSize: 13 }}>Couldn't register this device — try again.</p>
          )}
        </>
      )}

      <h2>Search radius</h2>
      <select value={radius} onChange={(e) => setRadius(Number(e.target.value))}>
        {RADII.map((r) => <option key={r} value={r}>{radiusLabel(r)}</option>)}
      </select>
      {radius >= 500 && (
        <p className="muted" style={{ fontSize: 12 }}>Casting a wide net — you'll see people far outside your area.</p>
      )}

      <h2>Age range</h2>
      <div className="slider-block">
        <div className="slider-head"><span>Minimum age</span><b>{ageMin}</b></div>
        <input type="range" min={18} max={99} step={1} value={ageMin}
          onChange={(e) => setAgeMin(Math.min(Number(e.target.value), ageMax))} />
        <div className="slider-head"><span>Maximum age</span><b>{ageMax}</b></div>
        <input type="range" min={18} max={99} step={1} value={ageMax}
          onChange={(e) => setAgeMax(Math.max(Number(e.target.value), ageMin))} />
        <p className="muted" style={{ fontSize: 12 }}>Only show people aged {ageMin}–{ageMax}.</p>
      </div>

      {/* Auto-save status row (iOS autosaveStatusRow). */}
      <div className="autosave-row">
        {autosave === "saving" && <><span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Saving…</>}
        {autosave === "saved" && <>✅ All changes saved</>}
        {autosave === "incomplete" && <>⚠️ Add the required fields and your changes will save</>}
        {autosave === "idle" && <>Changes save automatically</>}
      </div>

      {error && <div className="error">{error}</div>}

      {showPreview && (
        <ProfileDetail profile={previewProfile} isPreview onClose={() => setShowPreview(false)} />
      )}
      {myResponsiveness !== null && (
        <ResponsivenessInfoSheet
          pct={myResponsiveness}
          isSelf
          open={showRespInfo}
          onClose={() => setShowRespInfo(false)}
        />
      )}
    </div>
  );
}
